// Firebase Phone Auth, used only to prove the user owns a mobile number. Firebase sends the SMS
// and checks the code; we take the resulting ID token to our backend (/auth/otp/sms/login or
// /tenant/forgot-password/sms), which turns it into a normal Fire360Pro session. The Firebase
// session itself is kept in memory only and discarded straight after.
//
// The SDK is loaded on demand so the password login page doesn't pay for it.

let authPromise = null;

function getFirebaseAuth(config) {
  if (!authPromise) {
    authPromise = (async () => {
      const [{ initializeApp, getApps }, { initializeAuth, inMemoryPersistence }] = await Promise.all([
        import("firebase/app"),
        import("firebase/auth"),
      ]);
      const name = "fire360pro-otp";
      const app = getApps().find((a) => a.name === name) || initializeApp(config, name);
      const auth = initializeAuth(app, { persistence: inMemoryPersistence });
      auth.languageCode = "en";
      return auth;
    })().catch((e) => { authPromise = null; throw e; });
  }
  return authPromise;
}

// Sends the SMS. `container` is an empty DOM element for the invisible reCAPTCHA. Returns an
// opaque handle to pass to confirmSmsCode.
export async function sendSmsCode(config, phoneE164, container) {
  const auth = await getFirebaseAuth(config);
  const { RecaptchaVerifier, signInWithPhoneNumber } = await import("firebase/auth");
  // A fresh element per attempt: a reCAPTCHA can't be rendered twice into the same node.
  container.innerHTML = "";
  const el = document.createElement("div");
  container.appendChild(el);
  const verifier = new RecaptchaVerifier(auth, el, { size: "invisible" });
  try {
    return await signInWithPhoneNumber(auth, phoneE164, verifier);
  } finally {
    verifier.clear();
  }
}

// Checks the code with Firebase and returns the ID token for our backend.
export async function confirmSmsCode(confirmation, code) {
  const cred = await confirmation.confirm(code);
  const idToken = await cred.user.getIdToken();
  try { await (await authPromise).signOut(); } catch { /* in-memory only; nothing to clean up */ }
  return idToken;
}

const MESSAGES = {
  "auth/invalid-phone-number": "That mobile number doesn't look right.",
  "auth/missing-phone-number": "Enter your mobile number.",
  "auth/invalid-verification-code": "Wrong code — check the SMS and try again.",
  "auth/code-expired": "That code has expired. Request a new one.",
  "auth/too-many-requests": "Too many attempts from this device. Try again later.",
  "auth/quota-exceeded": "SMS limit reached for now. Try again later or use another sign-in method.",
  "auth/captcha-check-failed": "Security check failed. Reload the page and try again.",
  "auth/network-request-failed": "Network error. Check your connection and try again.",
  "auth/operation-not-allowed": "SMS sign-in isn't enabled yet. Use another sign-in method.",
  "auth/unauthorized-domain": "SMS sign-in isn't enabled for this website yet. Use another sign-in method.",
};

export function smsErrorMessage(err, fallback = "Something went wrong. Try again.") {
  if (err?.response?.data?.detail && typeof err.response.data.detail === "string") return err.response.data.detail;
  return MESSAGES[err?.code] || fallback;
}
